import { useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";

const errorMessage = (error: unknown) =>
  (error as { body?: { message?: string } })?.body?.message ??
  (error as Error)?.message ??
  String(error);

/**
 * In the profile: the user's own Telegram chat_id (PUT /api/me/telegram,
 * saved when the field is left or Enter is pressed) and «Проверить», which
 * saves the typed value, then has the server send a test message to the
 * saved chat_id (POST /api/me/telegram/test).
 */
export const TelegramChatField = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const { data, isPending } = useGetOne<Sale>(
    "sales",
    { id: identity?.id as Sale["id"] },
    { enabled: identity?.id != null },
  );
  const saved = data?.telegram_chat_id ?? "";
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  const id = useId();
  useEffect(() => setValue(saved), [saved]);

  if (identity?.id == null || isPending) return null;

  const fail = (error: unknown) =>
    notify(errorMessage(error), {
      type: "error",
      messageArgs: { _: errorMessage(error) },
    });

  const save = async () => {
    const next = value.trim();
    if (next === saved) return true;
    try {
      await dataProvider.setMyTelegramChatId(next);
      notify("crm.telegram.saved", { type: "success" });
      await queryClient.invalidateQueries({ queryKey: ["sales"] });
      return true;
    } catch (error) {
      fail(error);
      setValue(saved);
      return false;
    }
  };

  const check = async () => {
    setBusy(true);
    try {
      if (!(await save())) return;
      // the server tests the saved chat_id: the field was saved just above
      await dataProvider.testTelegram();
      notify("crm.telegram.sent", { type: "success" });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {translate("crm.telegram.chat_id")}
      </label>
      <div className="flex gap-2 min-w-0">
        <Input
          id={id}
          value={value}
          inputMode="numeric"
          autoComplete="off"
          maxLength={21}
          placeholder="123456789"
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => void save()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void save();
            }
          }}
          className="h-9 min-w-0 flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0"
          disabled={busy || !value.trim()}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => void check()}
        >
          <Send className="size-4" />
          {translate("crm.telegram.check")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("crm.telegram.hint")}
      </p>
    </div>
  );
};
